<template>
  <v-dialog
    v-model="dialog"
    width="650"
    @keydown.esc="closeDialog()"
  >
    <div
      aria-modal="true"
      aria-labelledby="modal-label"
      role="dialog">
      <v-card style="width: 100%;">
        <div class="stripe primary" />
        <div class="d-flex justify-end">
          <button
            class="font-default-size ma-3 clickable"
            role="button"
            @click="closeDialog()"
          >
            Close
          </button>
        </div>
        <div
          v-if="!submitted"
          class="d-flex justify-center"
        >
          <mail-icon
            :color="
              siteConfig.navBottomBorderColor === 'green' ? 'accent' : 'gold'
            "
          />
        </div>
        <transition
          name="spin"
          mode="out-in"
        >
          <div
            v-if="!submitted"
            key="form"
            class="pa-7 text-center"
            style="width: 100%;"
          >
            <h1 id="modal-label" class="secondary--text h3">
              {{
                isDownload
                  ? "Download our Common Sense Guide"
                  : "Stay in the loop with enrollment reminders"
              }}
            </h1>
            <p class="mx-10">
              {{
                isDownload
                  ? "Enter your email address to download the guide"
                  : "Enter your email address to get future updates about enrollment options and deadlines."
              }}
            </p>
            <v-form
              ref="form"
              class="primary-form"
              style="text-align: start;"
            >
              <v-label for="email">
                Your email address
              </v-label>
              <base-input
                ref="emailInput"
                v-model="email"
                email
                required
                color="primary"
                id="email"
                placeholder="Email address"
                autofocus
              />
              <v-label for="confirmEmail">
                Confirm your email address
              </v-label>
              <base-input
                v-model="confirmedEmail"
                :rules="[v => v.toLowerCase() === email.toLowerCase() || 'Emails do not match']"
                color="primary"
                id="confirmEmail"
                placeholder="Email address"
                required
              />
              <small
                v-if="siteConfig"
                class="font-small"
                id="AgreeInstructions"
              >
                By providing your email address, you give permission for
                {{ siteConfig.careNetworkName }} to retain your email address for
                purposes of future communications. You can opt out of receiving
                email communications at any time.
              </small>
              <v-checkbox
                v-if="siteConfig"
                v-model="followUp"
                :label="'Yes, I\'d like to receive future communications from ' + siteConfig.careNetworkName"
                required
                aria-describedby="AgreeInstructions"
              />
              <div class="d-flex justify-center">
                <v-btn
                  :ripple="false"
                  text
                  type="submit"
                  :disabled="loading"
                  @click.prevent="submit()"
                >
                  <span
                    class="primary--text"
                    style="font-size: 18px; font-weight: bold;"
                  >Submit</span>
                  <v-icon
                    :color="siteConfig && siteConfig.navBottomBorderColor === 'green' ? 'accent' : 'gold'"
                    postfix
                  >
                    arrow_forward
                  </v-icon>
                </v-btn>
              </div>
              <base-loader
                class="mt-3"
                :loading="loading"
              />
              <p
                v-if="didError && siteConfig"
                class="error--text mt-4"
              >
                Something went wrong! Look's like your request could not be completed. If this problem persists for you please reach out
                to us directly at {{ siteConfig.customerContactPhone | formatPhone }}
              </p>
            </v-form>
          </div>
          <div
            v-if="submitted && !didError"
            class="pt-5"
            style="height: 300px"
          >
            <confirmation-snippet
              key="confirmation"
              :message="!isDownload
                ? 'Our team will keep you informed as enrollment approaches.'
                : 'Your download should begin automatically.'
              "
            />
            <div class="d-flex justify-center">
              <base-button
                text="Close"
                @click="closeDialog()"
              />
            </div>
          </div>
        </transition>
      </v-card>
    </div>
  </v-dialog>
</template>

<script>
import { mapState, mapMutations, mapActions } from 'vuex'
import ConfirmationSnippet from '@/components/snippets/confirmation-snippet'
import MailIcon from '@/components/snippets/mail-icon'
import { FormTypes, NATIONS_TYPES } from '@/store/modules/nations'
import BaseLoader from '@/components/base/base-loader'
import PixelTracker from '@/components/base/pixel-tracker'

export default {
  name: 'EmailDialog',
  components: {
    BaseLoader,
    ConfirmationSnippet,
    MailIcon
  },
  props: {
    value: {
      type: Boolean,
      required: true
    },
    emailReceived: {
      type: String,
      default: ''
    },
    isDownload: {
      type: Boolean,
      default: false
    }
  },
  data () {
    return {
      submitted: false,
      email: '',
      confirmedEmail: '',
      followUp: true,
      dialog: false
    }
  },
  computed: {
    ...mapState({
      siteConfig: state => state.site.siteConfig,
      loading: state => state.nations.loading,
      didError: state => state.nations.didError
    })
  },
  watch: {
    value: {
      handler () {
        this.dialog = this.value
      },
      immediate: true
    },
    dialog () {
      if (!this.dialog) {
        this.resetDialog()
        this.setDidError(false)
      } else {
        this.email = this.emailReceived
      }
      this.$emit('input', this.dialog)
    },
    emailReceived () {
      this.email = this.emailReceived
    }
  },
  methods: {
    ...mapMutations({
      setDidError: NATIONS_TYPES.mutations.SET_DID_ERROR
    }),
    ...mapActions({
      submitLead: NATIONS_TYPES.actions.submitLead
    }),
    submit () {
      if (this.validate(this.$refs.form)) {
        const model = {
          firstName: 'Newsletter',
          lastName: 'Signup',
          email: this.email,
          type: (this.isDownload)
            ? FormTypes.commonSense
            : FormTypes.newsletterSignUp,
          followUp: this.followUp
        }
        if (this.isDownload) {
          this.siteConfig.careNetworkName.indexOf('Vermont') !== -1 ? PixelTracker.track('608189871', 'wJDbCKTlt9kBEK_7gKIC') : PixelTracker.track('601117795', 'AQN6CPeb-9sBEOOo0Z4C')
        } else {
          this.siteConfig.careNetworkName.indexOf('Vermont') !== -1 ? PixelTracker.track('608189871', 'gek-CJ2tqNkBEK_7gKIC') : PixelTracker.track('601117795', 'bqSuCPTcltwBEOOo0Z4C')
        }
        this.submitLead(model)
          .then(() => {
            if (this.didError) {
              if (this.$analytics) {
                this.$analytics.track(this.isDownload ? 'Request_a_Guide_Submit_Failed' : 'Enrollment_Reminder_Submit_Failed')
              }
              return
            }
            this.submitted = true
            if (this.$analytics) {
              this.$analytics.track(this.isDownload ? 'Request_a_Guide_Submit' : 'Enrollment_Reminder_Submit')
            }
            if (this.isDownload) {
              this.$emit('download')
            }
          })
      }
    },
    validate (form) {
      const errs = form.inputs.filter(function (input) {
        return !input.validate(true)
      })
      if (errs.length) {
        errs[0].focus()
        return false
      } else {
        return true
      }
    },
    resetDialog () {
      this.email = ''
      this.confirmedEmail = ''
      this.submitted = false
    },
    closeDialog () {
      this.dialog = false
      if (this.$parent.$refs.emailInput) {
        setTimeout(() => {
          this.$parent.$refs.emailInput.focus()
        })
      }
    }
  }
}
</script>

<style lang="scss" scoped>
.stripe {
  width: 100%;
  height: 15px;
}

.v-btn {
  text-transform: none !important;
}
</style>
